import type { Schedule, ScreenTimeBudget } from '@kidshub/contracts';
import { useTranslation } from 'react-i18next';
import { toMinutes } from '../lib/duration.js';
import { Card, Icon } from '../ui/kit.js';
import { projectScreenTime } from './projection.js';

/**
 * What these settings actually mean, in plain language.
 *
 * The heart of the screen, and the reason the screen exists as one page rather
 * than five toggles: the settings are meaningful together, not separately. A
 * parent should be able to read the consequence before saving, including the
 * consequence they did not intend.
 *
 * The one rule stated out loud is `SPEC.md §17.1` — when the per-type budgets
 * add up to more than the day, the day wins. That interaction was undefined in
 * earlier drafts and is exactly what a parent would otherwise discover from
 * their child.
 */

interface OutcomeBlockProps {
  childName: string;
  budget: ScreenTimeBudget;
  schedule: Schedule;
}

export const OutcomeBlock = ({ childName, budget, schedule }: OutcomeBlockProps) => {
  const { t } = useTranslation();
  const projection = projectScreenTime({ budget, schedule });

  const scaleSeconds = Math.max(
    projection.dailyTotalSeconds,
    projection.configuredTypeTotalSeconds,
    1,
  );
  const percentOf = (seconds: number) => (seconds / scaleSeconds) * 100;

  return (
    <Card title={t('screenTime.outcomeTitle')} className="outcome">
      <p className="outcome-sentence">
        {t('screenTime.outcomeSentence', {
          name: childName,
          minutes: toMinutes(projection.dailyTotalSeconds),
        })}
      </p>

      {projection.typeAllowances.length > 0 ? (
        <>
          <div className="sumbar">
            {projection.typeAllowances.map((allowance) => (
              <i
                key={allowance.contentType}
                style={{ width: `${percentOf(allowance.configuredSeconds)}%` }}
              >
                {t('screenTime.barSegment', {
                  type: t(`contentType.${allowance.contentType}`),
                  minutes: toMinutes(allowance.configuredSeconds),
                })}
              </i>
            ))}
            {projection.isOverCommitted ? (
              <span
                className="sumbar-marker"
                style={{ left: `${percentOf(projection.dailyTotalSeconds)}%` }}
                aria-hidden="true"
              />
            ) : null}
          </div>

          <div className="sumbar-scale" aria-hidden="true">
            <span>0</span>
            <span>
              {t('screenTime.barScale', { minutes: toMinutes(projection.dailyTotalSeconds) })}
            </span>
          </div>
        </>
      ) : (
        <p className="t-meta">{t('screenTime.noTypeBudgets')}</p>
      )}

      {projection.isOverCommitted ? (
        <p className="callout">
          <Icon name="alert" small /> {t('screenTime.overCommitted')}
        </p>
      ) : null}

      {projection.tightestDay === null ? (
        <p className="callout">
          <Icon name="alert" small /> {t('screenTime.nothingScheduled')}
        </p>
      ) : null}

      <ul className="outcome-notes">
        {projection.typeAllowances.map((allowance) => (
          <li key={allowance.contentType}>
            {t('screenTime.firstStop', {
              type: t(`contentType.${allowance.contentType}`),
              minutes: toMinutes(allowance.firstSessionSeconds),
              limit: t(`limit.${allowance.firstSessionLimit}`),
            })}
          </li>
        ))}
        {projection.tightestDay === null ? null : (
          <li>
            {projection.isBudgetReachableEveryDay
              ? t('screenTime.sessionsFit', {
                  sessions: projection.tightestDay.sessions,
                  sessionMinutes: toMinutes(budget.maxSessionSeconds),
                  restMinutes: toMinutes(budget.restPeriodSeconds),
                  dailyMinutes: toMinutes(projection.dailyTotalSeconds),
                })
              : t('screenTime.sessionsShort', {
                  day: t(`day.${projection.tightestDay.day}.long`),
                  sessions: projection.tightestDay.sessions,
                  usableMinutes: toMinutes(projection.tightestDay.usableSeconds),
                  dailyMinutes: toMinutes(projection.dailyTotalSeconds),
                })}
          </li>
        )}
      </ul>
    </Card>
  );
};
